import { createApi } from "@reduxjs/toolkit/query/react"
import { dreamsenderApi } from "@/core/api/services"
import { buildQueryString } from "@/utils/strings"
import { baseQueryWithReauth } from "@/core/api/baseQuery"
import type {
  AddAdminOrganizationPayload,
  AdminOrganization,
  SetOrganizationStatus,
  DashboardReporting,
} from "@/types/entities/organization"
import type { DashboardPayload } from "./organizationApi"
import type { PaginationProps } from "./types"

type AdminOrgQueryParams =
  | (PaginationProps & {
      owner?: string[]
      status?: "active" | "inactive" | "all"
    })
  | undefined

type AdminDashboardQueryBody = {
  filters: Omit<DashboardPayload, "id">
}

export const adminOrganizationApi = createApi({
  reducerPath: "adminOrganizationApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Admin Organization", "AdminDashboard"],
  endpoints: (builder) => ({
    getAdminOrganizations: builder.query<
      { items: Array<AdminOrganization>; total: number },
      AdminOrgQueryParams
    >({
      query: (params) => ({
        method: "GET",
        url: `admin/org/all?admin=true&${
          params ? buildQueryString(params) : ""
        }`,
      }),
      transformResponse: ({
        data,
      }: {
        data: { items: AdminOrganization[]; total: number }
      }) => {
        const itemsWithPermissions = data.items.map((org) => ({
          ...org,
          fullAccessUsers: org.fullAccessUsers || [],
          editAccessUsers: org.editAccessUsers || [],
          editAccessPages: org.editAccessPages || [],
          readAccessUsers: org.readAccessUsers || [],
          readAccessPages: org.readAccessPages || [],
          specialPermissions: org.specialPermissions || { dncListEdit: false },
        }))

        return {
          ...data,
          items: itemsWithPermissions,
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ _id }) => ({
                type: "Admin Organization" as const,
                id: _id,
              })),
              { type: "Admin Organization", id: "PARTIAL-LIST" },
            ]
          : [{ type: "Admin Organization", id: "PARTIAL-LIST" }],
    }),
    getSingleAdminOrganization: builder.query<
      AdminOrganization,
      { id: string }
    >({
      query: ({ id }) => ({
        method: "GET",
        url: `admin/org/all?admin=true&_id=${id}`,
      }),
      transformResponse: ({
        data,
      }: {
        data: { items: AdminOrganization[] }
      }) => {
        const org = data.items[0]
        if (org) {
          return {
            ...org,
            fullAccessUsers: org.fullAccessUsers || [],
            editAccessUsers: org.editAccessUsers || [],
            editAccessPages: org.editAccessPages || [],
            readAccessUsers: org.readAccessUsers || [],
            readAccessPages: org.readAccessPages || [],
            specialPermissions: org.specialPermissions || {
              dncListEdit: false,
            },
          }
        }
        return org
      },
      providesTags: (result) =>
        result
          ? [{ type: "Admin Organization", id: result._id }]
          : [{ type: "Admin Organization", id: "SINGLE" }],
    }),
    addAdminOrganization: builder.mutation<
      { data: AdminOrganization },
      AddAdminOrganizationPayload
    >({
      query: (body) => ({
        method: "POST",
        url: "admin/org",
        body: {
          ...body,
          division: "main",
        },
      }),
      invalidatesTags: ["Admin Organization"],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled
        if (data) {
          dispatch(dreamsenderApi.util.invalidateTags(["User"]))
        }
      },
    }),
    editAdminOrganization: builder.mutation<
      { data: AdminOrganization },
      {
        id: string
        body: AddAdminOrganizationPayload
      }
    >({
      query: ({ id, body }) => ({
        method: "PUT",
        url: `admin/org/${id}`,
        body: {
          ...body,
          division: "main",
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Admin Organization", id },
        { type: "Admin Organization", id: "PARTIAL-LIST" },
      ],
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
          dispatch(
            adminOrganizationApi.util.invalidateTags([
              { type: "Admin Organization", id },
              { type: "Admin Organization", id: "PARTIAL-LIST" },
              { type: "Admin Organization", id: "LIST" },
            ]),
          )
          dispatch(dreamsenderApi.util.invalidateTags(["User"]))
        } catch {
          // Query errors are exposed to callers by RTK Query.
        }
      },
    }),
    setAdminOrganizationStatus: builder.mutation<
      { data: AdminOrganization },
      { id: string; body: SetOrganizationStatus }
    >({
      query: ({ id, body }) => ({
        method: "PUT",
        url: `admin/org/${id}/status`,
        body,
      }),
      invalidatesTags: ["Admin Organization"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled
        if (data) {
          dispatch(dreamsenderApi.util.invalidateTags(["User"]))
        }
      },
    }),
    deleteAdminOrganization: builder.mutation<
      { data: AdminOrganization },
      { id: string }
    >({
      query: ({ id }) => ({
        method: "DELETE",
        url: `admin/org/${id}`,
      }),
      invalidatesTags: ["Admin Organization"],
    }),
    getAdminDashboardReporting: builder.query<
      DashboardReporting,
      Omit<AdminDashboardQueryBody, "organizationIds">
    >({
      query: (body) => ({
        url: `reporting/admin/dashboard`,
        method: "POST",
        body,
      }),
      providesTags: ["AdminDashboard"],
      transformResponse: ({ data }: { data: DashboardReporting }) => data,
    }),
  }),
})

export const {
  useGetAdminOrganizationsQuery,
  useGetSingleAdminOrganizationQuery,
  useAddAdminOrganizationMutation,
  useEditAdminOrganizationMutation,
  useSetAdminOrganizationStatusMutation,
  useDeleteAdminOrganizationMutation,
  useGetAdminDashboardReportingQuery,
} = adminOrganizationApi
